/* global gdAlertify */
import React, { useState, useEffect, useMemo } from "react";
import * as XLSX from "xlsx";
import axiosInstance from "../../config/axiosConfig";
import { calculateAge, formatDate, formatSectorLabel } from "../../utils/utils";

const cobroAdicionalMap = { 1: 'Paga SK', 2: 'Paga familia' };

const getTutor = (tutores, tipos) => (tutores || []).find((t) => tipos.includes(t.tipo_tutor)) || null;

// Grupos y campos tal como aparecen en el formulario de edición del paciente
// (forms/FormPacienteInicial.js, usado en /editar-paciente/:id).
const buildGrupos = (comunaNombreMap) => [
    {
        id: "paciente",
        label: "Datos Paciente",
        campos: [
            { key: "rut", label: "RUT", getValue: (p) => p.rut },
            { key: "nombres", label: "Nombres", getValue: (p) => p.nombres },
            { key: "apellidoPaterno", label: "Apellido Paterno", getValue: (p) => p.apellidoPaterno },
            { key: "apellidoMaterno", label: "Apellido Materno", getValue: (p) => p.apellidoMaterno },
            { key: "fechaNacimiento", label: "Fecha de Nacimiento", getValue: (p) => formatDate(p.fechaNacimiento) },
            { key: "edad", label: "Edad", getValue: (p) => calculateAge(p.fechaNacimiento) },
            { key: "fechaIngresoSk", label: "Fecha de Ingreso SK", getValue: (p) => formatDate(p.fecha_ingreso_sk) },
            { key: "diagnostico", label: "Diagnóstico o Patología", getValue: (p) => p.diagnostico },
            { key: "direccion", label: "Dirección", getValue: (p) => p.direccion },
            { key: "comoLlegar", label: "Cómo llegar", getValue: (p) => p.como_llegar },
            { key: "comuna", label: "Comuna", getValue: (p) => p.desc_comuna },
            { key: "sector", label: "Sector", getValue: (p) => formatSectorLabel(p.sector) },
            { key: "viveFueraSantiago", label: "Vive fuera de Santiago", getValue: (p) => (p.vive_fuera_santiago == 1 ? "Si" : "No") },
            { key: "requiereVisita", label: "Requiere visita", getValue: (p) => (p.requiere_visita == null || p.requiere_visita == 1 ? "Si" : "No") },
            { key: "observacionVisita", label: "Observación visita", getValue: (p) => p.observacion_visita },
            { key: "cobroAdicional", label: "Cobro adicional por defecto", getValue: (p) => cobroAdicionalMap[p.tipo_cobro_adicional_default] || "" },
            { key: "correosNotificacion", label: "Correos de notificación", getValue: (p) => p.correos_notificacion },
            { key: "estado", label: "Estado", getValue: (p) => (p.estado == 1 ? "Activo" : "Retirado") },
            { key: "motivoRetiro", label: "Motivo Retiro", getValue: (p) => p.motivo_retiro },
            { key: "fechaRetiro", label: "Fecha Retiro", getValue: (p) => formatDate(p.fecha_retiro) },
        ],
    },
    {
        id: "facturacion",
        label: "Datos Facturación",
        campos: [
            { key: "fact_facturarA", label: "Facturar A", getValue: (p) => ({ paciente: "Paciente", tutor: "Tutor", otro: "Otro" }[p.datosFacturacion?.[0]?.facturarA] || "") },
            { key: "fact_razonSocial", label: "Nombre / Razón Social", getValue: (p) => p.datosFacturacion?.[0]?.razonSocial },
            { key: "fact_rut", label: "RUT Facturación", getValue: (p) => p.datosFacturacion?.[0]?.rut },
            { key: "fact_giro", label: "Glosa", getValue: (p) => p.datosFacturacion?.[0]?.giro },
            { key: "fact_direccion", label: "Dirección Facturación", getValue: (p) => p.datosFacturacion?.[0]?.direccion },
            { key: "fact_comuna", label: "Comuna Facturación", getValue: (p) => comunaNombreMap[p.datosFacturacion?.[0]?.comuna] || "" },
            { key: "fact_email", label: "Email Facturación", getValue: (p) => p.datosFacturacion?.[0]?.email },
        ],
    },
    {
        id: "tutorSalud",
        label: "Datos Tutor Salud",
        campos: [
            { key: "ts_nombres", label: "Nombres Tutor Salud", getValue: (p) => getTutor(p.tutores, [2, 3])?.nombres },
            { key: "ts_apellidoPaterno", label: "Apellido Paterno Tutor Salud", getValue: (p) => getTutor(p.tutores, [2, 3])?.apellidoPaterno },
            { key: "ts_apellidoMaterno", label: "Apellido Materno Tutor Salud", getValue: (p) => getTutor(p.tutores, [2, 3])?.apellidoMaterno },
            { key: "ts_relacion", label: "Relación Tutor Salud", getValue: (p) => getTutor(p.tutores, [2, 3])?.relacion },
            { key: "ts_correo", label: "Correo Tutor Salud", getValue: (p) => getTutor(p.tutores, [2, 3])?.correo },
            { key: "ts_celular", label: "Celular Tutor Salud", getValue: (p) => getTutor(p.tutores, [2, 3])?.celular },
        ],
    },
    {
        id: "tutorPago",
        label: "Datos Tutor Pago",
        campos: [
            { key: "tp_nombres", label: "Nombres Tutor Pago", getValue: (p) => getTutor(p.tutores, [1, 3])?.nombres },
            { key: "tp_apellidoPaterno", label: "Apellido Paterno Tutor Pago", getValue: (p) => getTutor(p.tutores, [1, 3])?.apellidoPaterno },
            { key: "tp_apellidoMaterno", label: "Apellido Materno Tutor Pago", getValue: (p) => getTutor(p.tutores, [1, 3])?.apellidoMaterno },
            { key: "tp_relacion", label: "Relación Tutor Pago", getValue: (p) => getTutor(p.tutores, [1, 3])?.relacion },
            { key: "tp_correo", label: "Correo Tutor Pago", getValue: (p) => getTutor(p.tutores, [1, 3])?.correo },
            { key: "tp_celular", label: "Celular Tutor Pago", getValue: (p) => getTutor(p.tutores, [1, 3])?.celular },
        ],
    },
    {
        id: "turnos",
        label: "Datos de Turnos",
        campos: [
            { key: "tipoTurno", label: "Tipo Turno", getValue: (p) => p.turnos?.rotation_pattern_text },
            { key: "rotacion", label: "Rotación", getValue: (p) => p.turnos?.rotation_pattern_text2 },
            { key: "horaInicio", label: "Hora Inicio", getValue: (p) => p.turnos?.hora_inicio },
            { key: "horaFin", label: "Hora Fin", getValue: (p) => p.turnos?.hora_fin },
            { key: "diasServicio", label: "Días de Servicio", getValue: (p) => p.turnos?.days_service },
        ],
    },
    {
        id: "valorFamilia",
        label: "Valor servicio familia",
        campos: [
            { key: "valorTurnoPaciente", label: "Valor turno", getValue: (p) => p.valoresServicio?.[0]?.valorTurnoPaciente },
            { key: "valorTurnoBasePaciente", label: "Valor turno base", getValue: (p) => p.valoresServicio?.[0]?.valorTurnoBasePaciente },
            { key: "valorTurnoAlternativoPaciente", label: "Valor turno alternativo", getValue: (p) => p.valoresServicio?.[0]?.valorTurnoAlternativoPaciente },
            { key: "valorColacionPaciente", label: "Valor colación", getValue: (p) => p.valoresServicio?.[0]?.valorColacionPaciente },
            { key: "valorLocomocionPaciente", label: "Valor locomoción", getValue: (p) => p.valoresServicio?.[0]?.valorLocomocionPaciente },
            { key: "valorOtroPaciente", label: "Valor otro", getValue: (p) => p.valoresServicio?.[0]?.valorOtroPaciente },
        ],
    },
    {
        id: "valorAuxiliar",
        label: "Valor servicio auxiliar",
        campos: [
            { key: "valorTurnoAuxiliar", label: "Valor turno", getValue: (p) => p.valoresServicio?.[0]?.valorTurnoAuxiliar },
            { key: "valorTurnoBaseAuxiliar", label: "Valor turno base", getValue: (p) => p.valoresServicio?.[0]?.valorTurnoBaseAuxiliar },
            { key: "valorColacionAuxiliar", label: "Valor colación", getValue: (p) => p.valoresServicio?.[0]?.valorColacionAuxiliar },
            { key: "valorLocomocionAuxiliar", label: "Valor locomoción", getValue: (p) => p.valoresServicio?.[0]?.valorLocomocionAuxiliar },
            { key: "valorOtroAuxiliar", label: "Valor otro", getValue: (p) => p.valoresServicio?.[0]?.valorOtroAuxiliar },
        ],
    },
];

const ExportarPacientesModal = ({ isOpen, onClose, totalPacientes = 0, filtroParams = {} }) => {
    const grupos = useMemo(() => buildGrupos({}), []);
    const todosLosCampos = useMemo(() => grupos.flatMap((g) => g.campos.map((c) => c.key)), [grupos]);

    const [selectedFields, setSelectedFields] = useState(new Set());
    const [exportando, setExportando] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setSelectedFields(new Set(todosLosCampos));
        }
    }, [isOpen, todosLosCampos]);

    if (!isOpen) return null;

    const toggleCampo = (key) => {
        setSelectedFields((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    const toggleGrupo = (grupo, marcar) => {
        setSelectedFields((prev) => {
            const next = new Set(prev);
            grupo.campos.forEach((c) => {
                if (marcar) {
                    next.add(c.key);
                } else {
                    next.delete(c.key);
                }
            });
            return next;
        });
    };

    const seleccionarTodos = () => setSelectedFields(new Set(todosLosCampos));
    const deseleccionarTodos = () => setSelectedFields(new Set());

    const handleExportar = async () => {
        if (selectedFields.size === 0) {
            gdAlertify.notiError("Debe seleccionar al menos un dato del formulario.");
            return;
        }

        setExportando(true);
        try {
            const [pacientesResponse, comunasResponse] = await Promise.all([
                axiosInstance.get("/paciente/busqueda", { params: filtroParams }),
                axiosInstance.get("/params/comunas"),
            ]);

            const comunaNombreMap = {};
            (comunasResponse.data.comunas || []).forEach((comuna) => {
                comunaNombreMap[comuna.id] = comuna.nombre;
            });

            const gruposConMapas = buildGrupos(comunaNombreMap);
            const camposActivos = gruposConMapas
                .flatMap((g) => g.campos)
                .filter((c) => selectedFields.has(c.key));

            const pacientesData = pacientesResponse.data.data || [];

            const encabezado = camposActivos.map((c) => c.label);
            const filas = pacientesData.map((p) => camposActivos.map((c) => c.getValue(p) ?? ""));

            const worksheet = XLSX.utils.aoa_to_sheet([encabezado, ...filas]);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Pacientes");
            XLSX.writeFile(workbook, `nomina-pacientes-ficha-completa-${Date.now()}.xlsx`);

            gdAlertify.notiSuccess(`Se exportaron ${filas.length} pacientes.`);
            onClose();
        } catch (error) {
            console.error("Error al exportar pacientes:", error);
            gdAlertify.notiError("Ocurrió un error al exportar los datos.");
        } finally {
            setExportando(false);
        }
    };

    return (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" aria-hidden="true">
            <div className="modal-dialog modal-lg modal-dialog-scrollable">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Exportar Datos - Pacientes</h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>
                    <div className="modal-body">
                        <p className="text-muted small mb-2">
                            Seleccione los datos del formulario que quiere incluir en el Excel. Se exportará una fila por
                            cada uno de los {totalPacientes || ""} pacientes, con las columnas marcadas.
                        </p>
                        <div className="d-flex gap-2 mb-3">
                            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={seleccionarTodos}>
                                Seleccionar todos
                            </button>
                            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={deseleccionarTodos}>
                                Ninguno
                            </button>
                        </div>

                        {grupos.map((grupo) => {
                            const marcados = grupo.campos.filter((c) => selectedFields.has(c.key)).length;
                            const todosMarcados = marcados === grupo.campos.length;
                            return (
                                <div className="border rounded mb-3" key={grupo.id}>
                                    <div className="d-flex justify-content-between align-items-center px-3 py-2 bg-light border-bottom">
                                        <div className="form-check mb-0">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                id={`grupo-pac-${grupo.id}`}
                                                checked={todosMarcados}
                                                onChange={(e) => toggleGrupo(grupo, e.target.checked)}
                                            />
                                            <label className="form-check-label fw-semibold" htmlFor={`grupo-pac-${grupo.id}`}>
                                                {grupo.label}
                                            </label>
                                        </div>
                                        <span className="text-muted small">{marcados}/{grupo.campos.length}</span>
                                    </div>
                                    <div className="row px-3 py-2 g-2">
                                        {grupo.campos.map((campo) => (
                                            <div className="col-md-6" key={campo.key}>
                                                <div className="form-check">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        id={`campo-pac-${campo.key}`}
                                                        checked={selectedFields.has(campo.key)}
                                                        onChange={() => toggleCampo(campo.key)}
                                                    />
                                                    <label className="form-check-label" htmlFor={`campo-pac-${campo.key}`}>
                                                        {campo.label}
                                                    </label>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <div className="modal-footer d-flex justify-content-between align-items-center">
                        <span className="text-muted small">{selectedFields.size} dato(s) seleccionado(s)</span>
                        <div>
                            <button type="button" className="btn btn-secondary me-2" onClick={onClose} disabled={exportando}>
                                Cancelar
                            </button>
                            <button type="button" className="btn btn-success" onClick={handleExportar} disabled={exportando}>
                                {exportando ? "Exportando..." : "Exportar"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ExportarPacientesModal;
